import React from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { FaPhone, FaEnvelope, FaMapMarkerAlt, FaClock, FaInstagram, FaFacebook, FaChevronRight, FaTiktok } from 'react-icons/fa';
import { motion } from 'framer-motion';
import { withPublicUrl } from '../utils/publicPath';
import { useConsent } from '../contexts/ConsentContext';

const Footer: React.FC = () => {
  const { reopen } = useConsent();
  const accentColor = '#FC5000';
  const darkBg = '#111111';
  const darkerBg = '#080808';
  const textColor = '#e0e0e0';
  // Lightened text color for better readability on dark backgrounds
  const lightTextColor = 'rgba(255, 255, 255, 0.75)';

  const socialLinks = [
    { icon: FaInstagram, url: 'https://www.instagram.com/cartech.dornbirn/?hl=de' },
    { icon: FaFacebook, url: 'https://www.facebook.com/KFZCarTech/?locale=de_DE' },
    { icon: FaTiktok, url: 'https://www.tiktok.com/@cartech.dornbirn' }
  ];

  const quickLinks = [
    { label: 'Startseite', path: '/' },
    { label: 'Leistungen', path: '/leistungen' },
    { label: 'Fahrzeuge', path: '/fahrzeuge' },
    { label: 'Über uns', path: '/ueber-uns' },
    // { label: 'News', path: '/news' }, // disabled for launch
    { label: 'Kontakt', path: '/kontakt' }
  ];

  return (
    <footer style={{ backgroundColor: darkBg, color: textColor, fontFamily: "'Inter', sans-serif" }}>
      {/* Main Footer Content */}
      <div className="py-5">
        <Container>
          <Row className="gy-5">
            {/* Brand & About */}
            <Col lg={4} md={6}>
              <div className="mb-4">
                <img 
                  src={withPublicUrl('/images/logos/logo.png')} 
                  alt="CarTech Logo" 
                  height="45"
                  style={{ filter: 'brightness(0) invert(1)', marginBottom: '1.5rem' }}
                />
                <p style={{ lineHeight: '1.8', maxWidth: '300px', color: lightTextColor }}>
                  DIE KFZ-Spezialisten
                  für Lack, Karosserie und Mechanik
                </p>
              </div>
              <div className="d-flex gap-3">
                {socialLinks.map((social, index) => (
                  <motion.a
                    key={index}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    whileHover={{ y: -3, color: accentColor }}
                    style={{ 
                      color: '#fff', 
                      fontSize: '1.25rem',
                      transition: 'color 0.3s ease',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      backgroundColor: 'rgba(255,255,255,0.05)'
                    }}
                  >
                    <social.icon />
                  </motion.a>
                ))}
              </div>
            </Col>

            {/* Quick Links */}
            <Col lg={2} md={6}>
              <h5 className="mb-4 fw-bold text-white">Navigation</h5>
              <ul className="list-unstyled">
                {quickLinks.map((link, index) => (
                  <li key={index} className="mb-3">
                    <Link 
                      to={link.path} 
                      className="text-decoration-none d-flex align-items-center group-hover-link"
                      style={{ color: textColor, transition: 'all 0.3s ease' }}
                    >
                      <motion.span 
                        whileHover={{ x: 5, color: accentColor }}
                        style={{ display: 'inline-flex', alignItems: 'center' }}
                      >
                        <FaChevronRight size={10} style={{ marginRight: '8px', color: accentColor }} />
                        {link.label}
                      </motion.span>
                    </Link>
                  </li>
                ))}
              </ul>
            </Col>

            {/* Contact Info */}
            <Col lg={3} md={6}>
              <h5 className="mb-4 fw-bold text-white">Kontakt</h5>
              <ul className="list-unstyled">
                <li className="mb-4 d-flex">
                  <div style={{ color: accentColor, marginTop: '4px', marginRight: '15px' }}>
                    <FaMapMarkerAlt size={18} />
                  </div>
                  <div>
                    <span className="d-block fw-bold text-white">Standort</span>
                    <span style={{ color: lightTextColor }}>Wallenmahd 23/C3<br/>6850 Dornbirn, Österreich</span>
                  </div>
                </li>
                <li className="mb-4 d-flex">
                  <div style={{ color: accentColor, marginTop: '4px', marginRight: '15px' }}>
                    <FaPhone size={18} />
                  </div>
                  <div>
                    <span className="d-block fw-bold text-white">Telefon</span>
                    <a href="tel:+436642508088" className="text-decoration-none hover-accent" style={{ color: lightTextColor }}>
                      +43 664 2508088
                    </a>
                  </div>
                </li>
                <li className="mb-3 d-flex">
                  <div style={{ color: accentColor, marginTop: '4px', marginRight: '15px' }}>
                    <FaEnvelope size={18} />
                  </div>
                  <div>
                    <span className="d-block fw-bold text-white">Email</span>
                    <a href="mailto:kontakt@cartech-dornbirn.at" className="text-decoration-none hover-accent" style={{ color: lightTextColor }}>
                      kontakt@cartech-dornbirn.at
                    </a>
                  </div>
                </li>
              </ul>
            </Col>

            {/* Opening Hours */}
            <Col lg={3} md={6}>
              <h5 className="mb-4 fw-bold text-white">Öffnungszeiten</h5>
              <div className="p-3 rounded-3" style={{ backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.05)' }}>
                <div className="d-flex align-items-start mb-3">
                  <FaClock style={{ color: accentColor, marginTop: '4px', marginRight: '12px', flexShrink: 0 }} />
                  <div>
                    <span className="d-block fw-bold text-white mb-1">Mo - Do</span>
                    <span className="d-block small" style={{ color: lightTextColor }}>08:00 - 12:00</span>
                    <span className="d-block small" style={{ color: lightTextColor }}>13:00 - 17:30</span>
                  </div>
                </div>
                <div className="d-flex align-items-start mb-3">
                  <FaClock style={{ color: accentColor, marginTop: '4px', marginRight: '12px', flexShrink: 0 }} />
                  <div>
                    <span className="d-block fw-bold text-white mb-1">Freitag</span>
                    <span className="d-block small" style={{ color: lightTextColor }}>08:00 - 12:00</span>
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </div>

      {/* Bottom Bar */}
      <div className="py-4" style={{ backgroundColor: darkerBg, borderTop: '1px solid rgba(255,255,255,0.05)' }}>
        <Container>
          <Row className="align-items-center">
            <Col md={6} className="text-center text-md-start mb-3 mb-md-0">
              <p className="mb-0 small" style={{ color: lightTextColor }}>
                &copy; {new Date().getFullYear()} CarTech Dornbirn. Alle Rechte vorbehalten.
              </p>
            </Col>
            <Col md={6} className="text-center text-md-end">
              <Link to="/datenschutz" className="text-decoration-none small me-4 hover-text-white" style={{ color: lightTextColor, transition: 'color 0.2s' }}>
                Datenschutz
              </Link>
              <Link to="/impressum" className="text-decoration-none small me-4 hover-text-white" style={{ color: lightTextColor, transition: 'color 0.2s' }}>
                Impressum
              </Link>
              <button
                type="button"
                onClick={reopen}
                className="text-decoration-none small footer-linkbtn"
                style={{ color: lightTextColor, background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontFamily: 'inherit', verticalAlign: 'baseline' }}
              >
                Cookie-Einstellungen
              </button>
            </Col>
          </Row>
        </Container>
      </div>
    </footer>
  );
};

export default Footer;
